import './need-access-modal.component.scss';

import type { FunctionComponent } from 'preact';
import { useEffect, useMemo } from 'preact/hooks';

import { LoginType } from '../../../auth/models/auth.model';
import { selectAccountSettings } from '../../../auth/store/auth.selectors';
import { checkAuthentication, loadAccountSettings } from '../../../auth/store/auth.thunk';
import { SpinnerComponent } from '../../../common/components/spinner/spinner.component';
import { selectNeedAccessLoading } from '../../../content/store/content.selectors';
import { t, useTranslation } from '../../../i18n';
import type { Membership } from '../../../membership/models/membership.model';
import type { AccountSettingsResponse } from '../../../models/common.model';
import type { Product } from '../../../shopify/models/product.model';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import type { NeedAccessContentType } from '../../need-access.interface';
import { MembershipsView } from './membership-view';
import { ProductsView } from './products-view';

interface NeedAccessModalProps {
  contentType: NeedAccessContentType;
  contentId: string;
  products: Product[];
  memberships: Membership[];
  modalTitle: string;
  close?: () => void;
}

export const NeedAccessModal: FunctionComponent<NeedAccessModalProps> = ({
  contentType,
  contentId,
  products,
  memberships,
  modalTitle = t('access.upgradeToAccess'),
  close,
}: NeedAccessModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const loading: boolean = useAppSelector(selectNeedAccessLoading);
  const accountSettingsResp: AccountSettingsResponse = useAppSelector(selectAccountSettings);
  const { accountSettings } = accountSettingsResp || {};

  useEffect(() => {
    dispatch(checkAuthentication({ loginTypes: [LoginType.CUSTOMER] }));
    dispatch(loadAccountSettings());
  }, [dispatch]);

  const cssVars = useMemo(() => {
    const vars: Record<string, string> = {};

    if (accountSettings?.cardBackgroundColor) {
      vars['--single-settings-bg-color'] = accountSettings.cardBackgroundColor;
    }
    if (accountSettings?.cardBorderColor) {
      vars['--single-settings-brdr-color'] = accountSettings.cardBorderColor;
    }
    if (accountSettings?.cardTextColor) {
      vars['--single-settings-text-color'] = accountSettings.cardTextColor;
    }
    if (accountSettings?.cardButtonBackgroundColor) {
      vars['--single-settings-button-bg-color'] = accountSettings.cardButtonBackgroundColor;
    }
    if (accountSettings?.cardButtonTextColor) {
      vars['--single-settings-button-text-color'] = accountSettings.cardButtonTextColor;
    }
    if (accountSettings?.borderWidth) {
      vars['--single-settings-border-width'] = `${accountSettings.borderWidth}px`;
    }
    if (accountSettings?.borderRadius) {
      vars['--single-settings-border-radius'] = `${accountSettings.borderRadius}px`;
    }

    return vars;
  }, [
    accountSettings?.cardBackgroundColor,
    accountSettings?.cardBorderColor,
    accountSettings?.cardTextColor,
    accountSettings?.cardButtonBackgroundColor,
    accountSettings?.cardButtonTextColor,
    accountSettings?.borderWidth,
    accountSettings?.borderRadius,
  ]);

  return (
    <div class="single-need-access-modal" style={cssVars}>
      {modalTitle && (
        <div class="single-modal-header">
          <h3 class="single-modal-title">{modalTitle}</h3>
        </div>
      )}

      <div class="single-modal-body">
        {loading && <SpinnerComponent />}

        {!loading && (
          <>
            {products?.length === 0 && memberships?.length === 0 && (
              <div class="single-product">{t('access.noProductsAvailable')}</div>
            )}

            <MembershipsView memberships={memberships} products={products} />

            <ProductsView products={products} />
          </>
        )}
      </div>

      <div class="single-modal-footer single-action-list inline--btns">
        <button class="btn btn-outline button" onClick={close}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
};
